import { Link } from "react-router-dom";
import { topics } from "@/mocks/topics";
import { faqs, testimonials } from "@/mocks/faqs";
import FaqAccordion from "@/components/feature/FaqAccordion";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import BackToTop from "@/components/feature/BackToTop";
import { useSeo } from "@/hooks/useSeo";

const principles = [
  {
    icon: "ri-time-line",
    title: "先讲过程，再谈结论",
    text: "热点事件往往在几天内多次反转。我们优先交代时间线，让读者自己判断，而不是急于给出定性。",
  },
  {
    icon: "ri-shield-check-line",
    title: "标注信息来源与状态",
    text: "每条内容都会区分「已核实」与「待核实」。只有一个信源的说法，我们只呈现线索，不当作事实。",
  },
  {
    icon: "ri-user-voice-line",
    title: "尊重当事人与目击者",
    text: "不使用带有侮辱性的称呼，不传播未经确认的私人信息。涉及当事人隐私的内容，一律不做放大。",
  },
  {
    icon: "ri-refresh-line",
    title: "有错就改，改了就说明",
    text: "当后续信息与原文不符时，我们会修订并在文末注明修改说明，让读者知道哪些内容发生了变化。",
  },
];

const milestones = [
  { label: "内容线", value: "现场直击 / 瓜田情报" },
  { label: "主题分区", value: "5 个方向" },
  { label: "更新节奏", value: "热点期间保持跟进" },
  { label: "内容语言", value: "简体中文" },
];

export default function About() {
  useSeo({
    title: "关于我们 - 看料大瓜 | 内容原则与联系方式",
    description:
      "了解看料大瓜的网站定位、发展理念与内容原则。我们专注现场直击与瓜田情报，标注信息来源与核实状态，并提供联系方式便于反馈更正。",
    keywords: "看料大瓜,吃瓜第一线,瓜田情报,现场直击,关于我们",
    canonical: "https://ahgslo.cn/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于看料大瓜",
        inLanguage: "zh-CN",
        description: "看料大瓜的网站定位、内容原则与联系方式。",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "看料大瓜",
        url: "https://ahgslo.cn/",
        email: "contact@kanliao-dagua.example.com",
        description: "聚焦热点事件现场直击的中文内容平台。",
      },
    ],
  });

  return (
    <main className="w-full">
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1240px] px-4 pt-6 md:px-6">
          <Breadcrumbs items={[{ label: "关于我们" }]} />
        </div>
        <div className="mx-auto max-w-[1240px] px-4 py-10 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-primary-600">
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                ABOUT US
              </span>
              <h1 className="mt-3 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
                关于看料大瓜
              </h1>
              <p className="mt-4 text-base leading-relaxed text-foreground-600">
                我们是一群习惯在第一线跟进热点的人。看料大瓜的出发点是：当一件事开始被反复讨论时，总有人只想知道「到底发生了什么」。这个网站，就是为这个需求而做的。
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {milestones.map((item) => (
                  <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                    <p className="text-xs text-foreground-500">{item.label}</p>
                    <p className="mt-2 text-sm font-medium text-foreground-900">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-lg font-black text-foreground-950">网站定位</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                看料大瓜不是资讯的搬运站，而是一个内容整理站。我们把分散的现场画面、目击者描述与后续进展，整理成可读性强的图文内容，让读者用更少的时间，获得更完整的理解。
              </p>
              <h2 className="mt-7 font-heading text-lg font-black text-foreground-950">发展理念</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                我们相信热度会过去，但准确的信息应当留存。因此比起追逐第一时间的刺激，我们更在意内容是否经得起几天后的回看。慢一点，但尽量靠谱。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto max-w-[1240px] px-4 py-14 md:px-6 md:py-20">
          <div className="max-w-2xl">
            <span className="mb-2 inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-primary-600">
              <span className="h-3 w-3 rounded-full border border-primary-400" />
              PRINCIPLES
            </span>
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              内容原则
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              这四条原则，决定了我们写什么、怎么写，以及出错之后怎么办。
            </p>
          </div>
          <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item) => (
              <article
                key={item.title}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-6"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100">
        <div className="mx-auto max-w-[1240px] px-4 py-14 md:px-6 md:py-20">
          <div className="max-w-2xl">
            <span className="mb-2 inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-primary-600">
              <span className="h-3 w-3 rounded-full border border-primary-400" />
              CHANNELS
            </span>
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              内容方向
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              目前覆盖五个现场方向，每一条线都在持续补充内容。
            </p>
          </div>
          <div className="mt-9 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {topics
              .filter((topic) => topic.slug !== "all")
              .map((topic) => (
                <Link
                  key={topic.slug}
                  to={`/live?cat=${topic.slug}`}
                  className="group flex cursor-pointer flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-100 text-primary-600">
                    <i className={`${topic.icon} text-lg`} />
                  </span>
                  <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
                    {topic.name}
                  </h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{topic.desc}</p>
                </Link>
              ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto max-w-[1240px] px-4 py-14 md:px-6 md:py-20">
          <div className="max-w-2xl">
            <span className="mb-2 inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-primary-600">
              <span className="h-3 w-3 rounded-full border border-primary-400" />
              VOICES
            </span>
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              读者怎么说
            </h2>
          </div>
          <div className="mt-9 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((item) => (
              <figure
                key={item.name}
                className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-6"
              >
                <i className="ri-double-quotes-l text-2xl text-accent-500" />
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {item.text}
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-200 font-heading text-sm font-bold text-secondary-900">
                    {item.name.slice(0, 1)}
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-medium text-foreground-900">{item.name}</span>
                    <span className="text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100">
        <div className="mx-auto grid max-w-[1240px] grid-cols-1 gap-10 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <span className="mb-2 inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-primary-600">
              <span className="h-3 w-3 rounded-full border border-primary-400" />
              CONTACT
            </span>
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              联系方式
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              内容纠错、线索提供或合作咨询，都欢迎写信给我们。请在邮件中尽量说明事件时间与信息来源，便于我们核实。
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <a
                href="mailto:contact@kanliao-dagua.example.com"
                className="flex cursor-pointer items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-5 py-4 transition-colors hover:border-primary-300"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-mail-send-line text-lg" />
                </span>
                <span className="flex flex-col">
                  <span className="text-xs text-foreground-500">内容纠错与线索</span>
                  <span className="text-sm font-medium text-foreground-900">
                    contact@kanliao-dagua.example.com
                  </span>
                </span>
              </a>
              <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-5 py-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                  <i className="ri-time-line text-lg" />
                </span>
                <span className="flex flex-col">
                  <span className="text-xs text-foreground-500">回复时间</span>
                  <span className="text-sm font-medium text-foreground-900">工作日 2 天内</span>
                </span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <h2 className="font-heading text-lg font-black text-foreground-950">常见问题</h2>
            <div className="mt-4">
              <FaqAccordion items={faqs.slice(0, 4)} />
            </div>
          </div>
        </div>
      </section>
      <BackToTop />
    </main>
  );
}